/*!
 * MAISON RJCP — interactions et animations du thème Shopify.
 * Conversion fidèle (mêmes valeurs, mêmes durées, mêmes courbes) des composants React :
 *   Landing.tsx, HeroVisual.tsx, SignupForm.tsx, WelcomeScene.tsx, lib/curtain.ts
 * Dépend de GSAP (assets/maison-rjcp-gsap.min.js), chargé juste avant.
 */
(function () {
  'use strict';

  var gsap = window.gsap;
  var MOTION_OK = '(prefers-reduced-motion: no-preference)';
  var KEY_TRANSITION = 'rjcp-transition'; // un cercle noir couvre la page de départ
  var KEY_SIGNED = 'rjcp-signup-ok'; // inscription validée par Shopify pendant cette session

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function store(method, key, value) {
    try {
      return window.sessionStorage[method](key, value);
    } catch (e) {
      return null;
    }
  }

  /* ─── Validation e-mail (identique à src/lib/validation.ts) ─────────────── */
  var EMAIL_RE = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,}$/i;
  function isValidEmail(raw) {
    var email = String(raw || '').trim().toLowerCase();
    if (email.length < 6 || email.length > 254) return false;
    var local = email.split('@')[0];
    if (!local || local.length > 64) return false;
    if (local.charAt(0) === '.' || local.charAt(local.length - 1) === '.' || email.indexOf('..') !== -1) return false;
    return EMAIL_RE.test(email);
  }

  /* ─── Transition « cercle » (lib/curtain.ts) ────────────────────────────── */
  function coverRadius() {
    return Math.hypot(window.innerWidth, window.innerHeight) / 2 + 2;
  }

  /** Un cercle noir naît au centre et s'agrandit jusqu'à couvrir la page. */
  function closeCircle(color) {
    return new Promise(function (resolve) {
      if (!gsap || reduced()) return resolve();
      var old = document.getElementById('rjcp-circle');
      if (old) old.remove();
      var el = document.createElement('div');
      el.id = 'rjcp-circle';
      el.setAttribute('aria-hidden', 'true');
      el.style.cssText = 'position:fixed;inset:0;z-index:300;pointer-events:auto;clip-path:circle(0px at 50% 50%);background:' + (color || 'var(--color-noir)');
      document.body.appendChild(el);
      var s = { r: 0 };
      gsap.to(s, {
        r: coverRadius(),
        duration: 1,
        ease: 'power3.inOut',
        onUpdate: function () {
          el.style.clipPath = 'circle(' + s.r.toFixed(1) + 'px at 50% 50%)';
        },
        onComplete: resolve,
      });
    });
  }

  /** À l'arrivée : le voile noir (posé dans <head>) s'ouvre depuis le centre. */
  function openArrival(delay) {
    var html = document.documentElement;
    if (!html.classList.contains('rjcp-arriving')) return;
    if (!gsap) return html.classList.remove('rjcp-arriving');
    var s = { r: 0 };
    gsap.to(s, {
      r: coverRadius(),
      duration: 1.1,
      delay: delay,
      ease: 'power3.inOut',
      onUpdate: function () {
        html.style.setProperty('--rjcp-r', s.r.toFixed(1) + 'px');
      },
      onComplete: function () {
        html.classList.remove('rjcp-arriving');
        html.style.removeProperty('--rjcp-r');
      },
    });
  }

  /** Version « sur place » : le cercle déjà posé s'ouvre (boutique protégée par mot de passe). */
  function openCircleInPlace(delay) {
    var el = document.getElementById('rjcp-circle');
    if (!el) return;
    var s = { r: 0 };
    el.style.clipPath = 'none';
    var paint = function () {
      var m = 'radial-gradient(circle at 50% 50%, transparent ' + s.r.toFixed(1) + 'px, #000 ' + (s.r + 1).toFixed(1) + 'px)';
      el.style.maskImage = m;
      el.style.webkitMaskImage = m;
    };
    paint();
    gsap.to(s, { r: coverRadius(), duration: 1.1, delay: delay, ease: 'power3.inOut', onUpdate: paint, onComplete: function () { el.remove(); } });
  }

  /** Quitte la page avec le cercle noir, puis charge l'adresse donnée. */
  function leaveTo(url) {
    closeCircle('var(--color-noir)').then(function () {
      if (!reduced()) store('setItem', KEY_TRANSITION, '1');
      window.location.assign(url);
    });
  }

  /* ─── Landing : animation d'entrée (Landing.tsx) ────────────────────────── */
  function initLandingIntro(root) {
    if (!gsap) return;
    var mm = gsap.matchMedia();
    mm.add(MOTION_OK, function () {
      var q = gsap.utils.selector(root);
      gsap
        .timeline({ delay: 0.35 })
        .fromTo(q('[data-logo]'), { autoAlpha: 0, y: -14 }, { autoAlpha: 1, y: 0, duration: 1.1, ease: 'power3.out' })
        .fromTo(q('[data-line]'), { yPercent: 110, y: 0 }, { yPercent: 0, y: 0, duration: 1.3, ease: 'power4.out', stagger: 0.1 }, 0.25)
        .fromTo(q('[data-fade]'), { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 1, ease: 'power3.out', stagger: 0.12 }, 0.85);
    });
  }

  /* ─── Photographie en côtes (HeroVisual.tsx) ────────────────────────────── */
  var SLATS = 6;

  function initHeroIntro(el) {
    if (!gsap) return;
    var mm = gsap.matchMedia();
    mm.add(MOTION_OK, function () {
      var slats = gsap.utils.toArray(el.querySelectorAll('[data-slat]'));
      var cols = gsap.utils.toArray(el.querySelectorAll('[data-col]'));
      var mid = (SLATS - 1) / 2;

      var tl = gsap.timeline({ delay: 0.2 });
      tl.fromTo(
        slats,
        { clipPath: function (i) { return i % 2 ? 'inset(0% 0% 100% 0%)' : 'inset(100% 0% 0% 0%)'; } },
        { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.4, ease: 'power4.inOut', stagger: { each: 0.09, from: 'center' } }
      )
        .fromTo(
          cols,
          { yPercent: function (i) { return (i % 2 ? -1 : 1) * (5 + Math.abs(i - mid) * 1.5); } },
          { yPercent: 0, duration: 2.4, ease: 'power3.out', stagger: { each: 0.09, from: 'center' } },
          0
        )
        .fromTo(el.querySelectorAll('img'), { scale: 1.14 }, { scale: 1, duration: 2.8, ease: 'power2.out' }, 0);

      // Respiration au pointeur (souris uniquement)
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      var movers = cols.map(function (c) { return gsap.quickTo(c, 'y', { duration: 1.4, ease: 'power3.out' }); });
      var onMove = function (e) {
        var nx = (e.clientX / window.innerWidth) * 2 - 1;
        var ny = (e.clientY / window.innerHeight) * 2 - 1;
        movers.forEach(function (to, i) { to((i % 2 ? 1 : -1) * ny * 9 + (i - mid) * nx * 2.5); });
      };
      tl.eventCallback('onComplete', function () { window.addEventListener('pointermove', onMove); });
      return function () { window.removeEventListener('pointermove', onMove); };
    });
  }

  /** Cercle de révélation : les vestes changent de couleur à l'intérieur (souris, ou toucher sur mobile). */
  function initReveal(el) {
    var ring = el.querySelector('[data-ring]');
    var hint = el.querySelector('[data-touch-hint]');
    var cols = Array.prototype.slice.call(el.querySelectorAll('[data-col]'));
    var layers = Array.prototype.slice.call(el.querySelectorAll('[data-reveal]'));
    if (!ring || !gsap) return;
    var isReduced = reduced();
    var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!finePointer && hint) {
      hint.classList.remove('opacity-0');
      hint.classList.add('opacity-100');
    }
    var hideHint = function () {
      if (!hint) return;
      hint.classList.remove('opacity-100');
      hint.classList.add('opacity-0');
    };

    var s = { x: 0, y: 0, tx: 0, ty: 0, r: 0 };
    var placed = false;
    var closeTimer = 0;
    var rTween = null;

    var radius = function () {
      var w = el.clientWidth;
      return Math.round(Math.min(Math.max(w * (finePointer ? 0.17 : 0.26), 80), 190));
    };
    var setRadius = function (to) {
      if (rTween) rTween.kill();
      if (isReduced) {
        s.r = to;
        return;
      }
      rTween = gsap.to(s, { r: to, duration: to ? 0.75 : 0.6, ease: to ? 'expo.out' : 'power3.inOut' });
    };
    var local = function (e) {
      var rect = el.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    var render = function () {
      var k = isReduced || !placed ? 1 : 0.2;
      s.x += (s.tx - s.x) * k;
      s.y += (s.ty - s.y) * k;
      placed = true;
      var r = Math.max(0, s.r);
      var base = el.getBoundingClientRect();
      for (var i = 0; i < cols.length; i++) {
        var c = cols[i].getBoundingClientRect();
        layers[i].style.clipPath =
          r < 0.5
            ? 'circle(0px at 0px 0px)'
            : 'circle(' + r.toFixed(1) + 'px at ' + (s.x - (c.left - base.left)).toFixed(1) + 'px ' + (s.y - (c.top - base.top)).toFixed(1) + 'px)';
      }
      ring.style.transform = 'translate3d(' + (s.x - r).toFixed(1) + 'px, ' + (s.y - r).toFixed(1) + 'px, 0)';
      ring.style.width = ring.style.height = (2 * r).toFixed(1) + 'px';
      ring.style.opacity = r < 0.5 ? '0' : '1';
    };

    var running = false;
    var start = function () {
      if (running) return;
      running = true;
      gsap.ticker.add(render);
    };
    window.setInterval(function () {
      if (s.r < 0.5 && running) {
        running = false;
        gsap.ticker.remove(render);
        render();
      }
    }, 400);

    var open = function (e, snap) {
      var p = local(e);
      s.tx = p.x;
      s.ty = p.y;
      if (snap || s.r < 1) {
        s.x = p.x;
        s.y = p.y;
      }
      start();
      setRadius(radius());
    };

    el.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'mouse') open(e, true);
    });
    el.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'mouse') {
        var p = local(e);
        s.tx = p.x;
        s.ty = p.y;
        if (s.r < 1) open(e, true);
      } else if (e.buttons || e.pressure > 0) {
        var t = local(e);
        s.tx = t.x;
        s.ty = t.y;
      }
    });
    el.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'mouse') setRadius(0);
    });
    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      window.clearTimeout(closeTimer);
      hideHint();
      open(e, true);
    });
    var up = function (e) {
      if (e.pointerType === 'mouse') return;
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(function () { setRadius(0); }, 2400);
    };
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    render();
  }

  /* ─── Formulaire d'inscription (SignupForm.tsx → formulaire client Shopify) ─ */
  function initSignup(landing) {
    var form = landing.querySelector('form[data-maison-form]');
    if (!form) return;
    var msgEl = landing.parentElement.querySelector('[data-maison-messages]') || document.querySelector('[data-maison-messages]');
    var M = {};
    try { M = JSON.parse(msgEl.textContent); } catch (e) {}

    var email = form.querySelector('[data-email]');
    var consent = form.querySelector('[data-consent]');
    var honeypot = form.querySelector('[data-honeypot]');
    var submit = form.querySelector('[data-submit]');
    var emailErr = form.querySelector('[data-email-error]');
    var consentErr = form.querySelector('[data-consent-error]');
    var formErr = form.querySelector('[data-form-error]');
    var labels = submit ? submit.querySelectorAll('[data-rib-label]') : [];
    var idleLabel = labels.length ? labels[0].textContent : '';
    var sending = false;

    var setEmailError = function (text) {
      emailErr.textContent = text || '';
      email.setAttribute('aria-invalid', text ? 'true' : 'false');
      if (text) {
        email.setAttribute('aria-describedby', 'maison-email-error');
        email.classList.remove('border-noir/30');
        email.classList.add('border-ink');
      } else {
        email.removeAttribute('aria-describedby');
        email.classList.remove('border-ink');
        email.classList.add('border-noir/30');
      }
    };
    var setConsentError = function (text) {
      consentErr.textContent = text || '';
      consent.setAttribute('aria-invalid', text ? 'true' : 'false');
      if (text) consent.setAttribute('aria-describedby', 'maison-consent-error');
      else consent.removeAttribute('aria-describedby');
    };
    var setFormError = function (text) {
      formErr.textContent = text || '';
      if (text) form.setAttribute('aria-describedby', 'maison-form-error');
      else form.removeAttribute('aria-describedby');
    };
    var setSending = function (on) {
      sending = on;
      submit.disabled = on;
      form.setAttribute('aria-busy', on ? 'true' : 'false');
      for (var i = 0; i < labels.length; i++) labels[i].textContent = on ? M.sending || 'Ouverture…' : idleLabel;
    };

    email.addEventListener('input', function () {
      if (emailErr.textContent) setEmailError(null);
    });
    consent.addEventListener('change', function () {
      if (consentErr.textContent) setConsentError(null);
    });

    /** Traduit l'état renvoyé par Shopify (data-signup-state) en message pour le visiteur. */
    var showShopifyErrors = function (stateEl) {
      var items = stateEl ? stateEl.querySelectorAll('[data-error-field]') : [];
      var shown = false;
      for (var i = 0; i < items.length; i++) {
        var field = items[i].getAttribute('data-error-field');
        var text = (items[i].textContent || '').trim();
        if (field === 'email' && /taken|already|déjà|existe|utilis/i.test(text)) {
          setFormError(M.alreadySubscribed);
          shown = true;
        } else if (field === 'email') {
          setEmailError(M.invalidEmail);
          email.focus();
          shown = true;
        } else if (!shown) {
          setFormError(text || M.generic);
          shown = true;
        }
      }
      if (!shown) setFormError(M.generic);
    };

    /** Le formulaire s'efface, puis le cercle noir couvre la page. */
    var leave = function () {
      var fade = reduced() || !gsap
        ? Promise.resolve()
        : new Promise(function (resolve) {
            gsap.to(landing.querySelectorAll('#inscription > *'), { autoAlpha: 0, y: -12, duration: 0.45, ease: 'power2.in', stagger: 0.05, onComplete: resolve });
          });
      return fade.then(function () { return closeCircle('var(--color-noir)'); });
    };

    // GSAP s'arrête dans un onglet en arrière-plan : on n'attend jamais l'animation plus de 2,5 s.
    var leaveThen = function (next) {
      Promise.race([leave(), new Promise(function (resolve) { window.setTimeout(resolve, 2500); })]).then(next);
    };

    /**
     * Si hCaptcha est activé pour les formulaires de contact (Boutique en ligne → Préférences → Protection
     * anti-spam), « captcha-bootstrap » lie un hCaptcha invisible à ce formulaire dès qu'on y entre :
     * la vérification se fait sur la landing ; une fois réussie, Shopify appelle form.submit tel qu'il était
     * au moment de la liaison, c'est-à-dire ce relais, qui joue la transition puis envoie le formulaire.
     * Le retour sur la landing est traité au démarrage (boot).
     */
    var verified = false;
    form.submit = function () {
      verified = true;
      // Appel de « captcha-bootstrap » après submitToShopify : la transition a déjà eu lieu.
      if (handOff) return HTMLFormElement.prototype.submit.call(form);
      leaveThen(function () {
        if (!reduced()) store('setItem', KEY_TRANSITION, '1');
        HTMLFormElement.prototype.submit.call(form);
      });
    };

    /**
     * Sans hCaptcha lié (script pas encore chargé, protection désactivée) : envoi confié à « captcha-bootstrap »,
     * qui ajoute son jeton form_key (sans lui, /contact répond « invalid parameters ») et ouvre si besoin
     * la page de vérification de Shopify.
     */
    var handOff = false;
    var submitToShopify = function () {
      handOff = true;
      if (!reduced()) store('setItem', KEY_TRANSITION, '1');
      if (form.requestSubmit) form.requestSubmit();
      else HTMLFormElement.prototype.submit.call(form);
    };

    // Page restaurée par le bouton « Précédent » : on retire le voile et on réactive le formulaire.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      var circle = document.getElementById('rjcp-circle');
      if (circle) circle.remove();
      handOff = false;
      verified = false;
      setSending(false);
      if (gsap) gsap.set(landing.querySelectorAll('#inscription > *'), { autoAlpha: 1, y: 0 });
    });

    form.addEventListener('submit', function (e) {
      // Deuxième passage : l'envoi continue vers Shopify et sa protection anti-spam.
      if (handOff) return;
      e.preventDefault();
      // Ni l'écouteur d'hCaptcha (il lancerait la vérification avant la validation ci-dessous)
      // ni celui de « captcha-bootstrap » (il renverrait le formulaire tout de suite) ne doivent réagir.
      e.stopImmediatePropagation();
      if (sending) return;
      setFormError(null);

      var ok = isValidEmail(email.value);
      setEmailError(ok ? null : M.invalidEmail);
      setConsentError(consent.checked ? null : M.consentRequired);
      if (!ok) return email.focus();
      if (!consent.checked) return consent.focus();
      // Champ piège rempli : robot. Rien n'est envoyé, rien n'est confirmé.
      if (honeypot && honeypot.value.trim() !== '') return;

      setSending(true);
      if (form.hasAttribute('data-hcaptcha-bound')) {
        // Vérification hCaptcha sur place, puis relais ci-dessus. Si le visiteur ferme la fenêtre
        // de vérification, Shopify ne rappelle rien : on réactive le bouton.
        verified = false;
        form.submit();
        window.setTimeout(function () { if (!verified) setSending(false); }, 4000);
      } else {
        leaveThen(submitToShopify);
      }
    });

    // Retour de l'envoi refusé par Shopify : le voile s'ouvre (boot) et les erreurs s'affichent.
    var initial = form.querySelector('[data-signup-state]');
    var initialState = initial ? initial.getAttribute('data-signup-state') : 'idle';
    if (initialState === 'error') {
      showShopifyErrors(initial);
      var target = document.getElementById('inscription');
      if (target) target.scrollIntoView({ block: 'center' });
    }
  }

  /* ─── Scène de bienvenue (WelcomeScene.tsx) ─────────────────────────────── */
  var OPEN_ANGLE = 108;

  function playWelcome(root, delay) {
    var heading = root.querySelector('[data-welcome-heading]');
    var focusHeading = function () { if (heading) heading.focus({ preventScroll: true }); };
    if (!gsap) return focusHeading();
    var q = gsap.utils.selector(root);
    var mainEl = root.querySelector('main');
    if (reduced()) {
      if (mainEl) mainEl.style.visibility = 'visible';
      gsap.set(q("[data-leaf='l']"), { rotateY: -OPEN_ANGLE });
      gsap.set(q("[data-leaf='r']"), { rotateY: OPEN_ANGLE });
      return focusHeading();
    }
    var main = root.querySelector('main');
    var text = root.querySelector('[data-text]');
    var lift = text ? text.offsetHeight / 2 + 24 : 0;

    var tl = gsap.timeline({ delay: delay, onComplete: focusHeading });
    // État de départ : la porte seule, au centre
    gsap.set(q('[data-stage]'), { y: lift });
    gsap.set(q('[data-line]'), { yPercent: 110 });
    gsap.set(q('[data-fade]'), { autoAlpha: 0, y: 14 });
    gsap.set(q('[data-heart]'), { scale: 0.4, autoAlpha: 0 });
    gsap.set(q('[data-door]'), { autoAlpha: 0 });
    if (main) main.style.visibility = 'visible';
    // 1. Apparition
    tl.fromTo(q('[data-door]'), { autoAlpha: 0, scale: 0.9 }, { autoAlpha: 1, scale: 1, duration: 0.6, ease: 'power3.out' });
    // 2. Ouverture
    tl.to(q("[data-leaf='l']"), { rotateY: -OPEN_ANGLE, duration: 0.9, ease: 'power3.inOut' }, 0.4)
      .to(q("[data-leaf='r']"), { rotateY: OPEN_ANGLE, duration: 0.9, ease: 'power3.inOut' }, '<')
      .to(q('[data-heart]'), { scale: 1, autoAlpha: 1, duration: 0.7, ease: 'back.out(2)' }, '<0.35')
      // 3. Le symbole se déplace, le titre apparaît
      .to(q('[data-stage]'), { y: 0, duration: 0.9, ease: 'power3.inOut' }, '<0.25')
      .to(q('[data-line]'), { yPercent: 0, duration: 0.9, ease: 'power4.out', stagger: 0.09 }, '<0.3')
      // 4. Confirmation
      .to(q('[data-fade]'), { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.12 }, '<0.45');
  }

  function bindGoHome(root, inPlace) {
    var links = root.querySelectorAll('[data-go-home]');
    for (var i = 0; i < links.length; i++) {
      links[i].addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        var href = this.getAttribute('href') || '/';
        if (inPlace) {
          closeCircle('var(--color-noir)').then(function () {
            if (!reduced()) store('setItem', KEY_TRANSITION, '1');
            window.location.reload();
          });
        } else {
          leaveTo(href);
        }
      });
    }
  }

  /** Boutique protégée par mot de passe : l'accueil se joue sur la même page. */
  function showWelcomeInPlace(landing) {
    var tpl = landing.querySelector('template[data-welcome-template]');
    if (!tpl) return;
    var scene = tpl.content.firstElementChild.cloneNode(true);
    landing.replaceWith(scene);
    window.scrollTo(0, 0);
    bindGoHome(scene, true);
    openCircleInPlace(0.1);
    playWelcome(scene, 0.55);
  }

  function initWelcomePage(wrapper) {
    var designMode = wrapper.getAttribute('data-design-mode') === 'true';
    // Ouverte directement, sans inscription validée pendant la session : retour à l'accueil.
    if (!designMode && store('getItem', KEY_SIGNED) !== '1') {
      window.location.replace(wrapper.getAttribute('data-home-url') || '/');
      return;
    }
    var scene = wrapper.querySelector('[data-maison-welcome]');
    bindGoHome(scene, false);
    openArrival(0.1);
    // Même délai que WelcomeScene.tsx : la scène démarre pendant que le cercle s'ouvre.
    playWelcome(scene, 0.55);
  }

  /* ─── Démarrage ─────────────────────────────────────────────────────────── */
  function boot() {
    // Sans GSAP (script bloqué), on affiche tout de suite le contenu, sans animation.
    if (!gsap) document.documentElement.classList.remove('maison-js', 'rjcp-arriving');
    var landing = document.querySelector('[data-maison-landing]');
    // Retour d'une inscription validée par Shopify : la page est encore sous le voile noir,
    // on enchaîne directement sur la bienvenue, qui l'ouvrira.
    if (landing && landing.querySelector('[data-signup-state="success"]')) {
      store('setItem', KEY_SIGNED, '1');
      if (!reduced()) store('setItem', KEY_TRANSITION, '1');
      window.location.replace(landing.getAttribute('data-welcome-url') || '/pages/bienvenue?view=bienvenue-rjcp');
      return;
    }
    if (landing) {
      openArrival(0.1);
      initLandingIntro(landing);
      var hero = landing.querySelector('[data-hero-visual]');
      if (hero) {
        initHeroIntro(hero);
        initReveal(hero);
      }
      initSignup(landing);
    }
    var welcome = document.querySelector('[data-maison-welcome-page]');
    if (welcome) initWelcomePage(welcome);
    if (!landing && !welcome) openArrival(0.1);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
